import { LineItem } from "../../../components/LineItems/LineItem";
import moment from 'moment-timezone';
import { LineItemTypes, ForSaleConfiguration, ShirtConfiguration} from "../../../components/LineItems/LineItemTypes/LineItemTypes";
import { chooseYourOwnPriceConfiguration } from "../../../components/LineItems/UnitPriceTypes/LineItemUnitPriceTypes";



/**
 * Functions to manipulate the shopping cart for a given session.
 */

export interface ShoppingCart 
{
    created: moment.Moment,
    lastUpdated: moment.Moment,
    lineItems?: cartLineItem[]
}

interface cartLineItem 
{
    details: LineItem,
    lastUpdated: moment.Moment
}

/**
 * Upserts the listed line item into the shopping cart
 * @param lineItem line item to upsert
 */
export const upsertToCart = (lineItem: LineItem) => 
{
    if(updateToCart(lineItem) !== true)
    {
        addToCart(lineItem);
    }
}

/**
 * Remove the listed item from the cart
 * @param lineItemDbId DbId of line item to remove
 * @returns true if item was removed, false if the item didn't exist in the cart.
 */
export const removeFromCart = (lineItemDbId: number): boolean => 
{
    const oldCart = getCart() ?? openCart();
    const index = oldCart.lineItems?.findIndex((item) => {return(item.details.itemConfiguration.dbId === lineItemDbId)});
    if(index === undefined)
    {
        return false;
    }

    //TODO: update Typescript so that we can use toSpliced to avoid needing a mutable method here.
    const newLineItems = oldCart.lineItems;
    newLineItems && newLineItems?.splice(index,1);
    const newCart =
    {
        ...oldCart,
        lastUpdated: moment(),
        lineItems: newLineItems
    }
    
    storeCart(newCart);
    window.dispatchEvent(new Event('ShoppingCartUpdateChange'));
    window.dispatchEvent(new Event('ShoppingCartSizeChange'));

    return true;
}

/**
 * Clear all items from the users cart
 */
export const clearCart = () => 
{
    sessionStorage.removeItem('shoppingCart');
    window.dispatchEvent(new Event('ShoppingCartUpdateChange'));
    window.dispatchEvent(new Event('ShoppingCartSizeChange'));
}

/**
 * Adds the listed line item into the shopping cart. Fails if item already exists.
 * @param lineItem line item to insert
 * @returns true if line item was added, false otherwise.
 */
export const addToCart = (lineItem: LineItem): boolean =>
{
    const oldCart = getCart() ?? openCart();
    //Verify if item already exists
    if(oldCart.lineItems?.find((item) => {return(item.details.itemConfiguration.dbId === lineItem.itemConfiguration.dbId)}))
    {
        return false;
    }
    //Item does not exist.
    const newItem = {details: lineItem, lastUpdated: moment()};
    const newCart =
    {
        ...oldCart,
        lastUpdated: moment(),
        lineItems: oldCart.lineItems ? oldCart.lineItems.concat(newItem) : new Array<cartLineItem>().concat(newItem)
    }

    storeCart(newCart);
    window.dispatchEvent(new Event('ShoppingCartSizeChange'));
    window.dispatchEvent(new Event('ShoppingCartUpdateChange'));


    return true;
}

/**
 * Updates the listed line item into the shopping cart. Fails if item does not exist.
 * @param lineItem line item to upsert
 * @returns true if the item was updated, false if the item doesn't exist.
 */
export const updateToCart = (lineItem: LineItem): boolean =>
{
    const oldCart = getCart() ?? openCart();
    //Check if item exists
    const itemIndex = oldCart.lineItems?.findIndex((item) => {return(item.details.itemConfiguration.dbId === lineItem.itemConfiguration.dbId)});
    if(itemIndex === undefined)
    {
        return false;
    }
    //Else, item does exist
    
    storeCart(
        {
            ...oldCart,
            lastUpdated: moment(),
            lineItems: oldCart.lineItems?.map((item, index) => {
                if (index===itemIndex)
                {
                    return ({
                        lastUpdated: moment(),
                        details: lineItem
                    })
                }
                else
                {
                    return(item)
                }
            })
        }
    );

    window.dispatchEvent(new Event('ShoppingCartSizeChange'));
    window.dispatchEvent(new Event('ShoppingCartUpdateChange'));

    return true;
}

/**
 * Retrieve the cart for the user. Returns undefined if no cart exists.
 */
export const getCart = (): ShoppingCart | undefined  => 
{
    const temp = sessionStorage.getItem('shoppingCart');
    return temp ? JSON.parse(temp) as ShoppingCart : undefined;
}

export const getLineitems = (): LineItem[] | undefined =>
{
   
    const cart = getCart();
    const tempLineitems: (LineItem[] | undefined) = cart?.lineItems?.map((item:cartLineItem) => item.details);

    // Expand this to remive line item from the price map, validity map, etc
    return tempLineitems;
}

/**
 * Creates a starting cart. Does not save the cart.
 * @returns the new shopping cart
 */
const openCart = (): ShoppingCart =>
{
    const baseCart = 
    {
        created: moment(),
        lastUpdated: moment(),
        lineItems: undefined
    }
    window.dispatchEvent(new Event('ShoppingCartUpdateChange'));
    return baseCart;
}

/**
 * Stores the cart into session storage. Will override existing cart.
 * @param cart: the cart to store
 */

const storeCart = (cart: ShoppingCart) =>
{
    sessionStorage.setItem('shoppingCart', JSON.stringify(cart));
}

export const getCartSize = () : number =>
{
    const cart = getCart();
    return cart ? checkCartSize(cart) : 0 ;
}

const checkCartSize = (cart: ShoppingCart): number =>
{
    return cart.lineItems?.filter((lineItem) => doesCountForSize(lineItem.details)).length ?? 0;
}

const doesCountForSize = (lineItem: LineItem) =>
{
    if(lineItem.itemFormat === LineItemTypes.ForSale)
    {
        return ((lineItem.itemConfiguration as ForSaleConfiguration).quantity ?? 0) > 0;
    }
    else if(lineItem.itemFormat === LineItemTypes.Shirts)
    {
        return ((lineItem.itemConfiguration as ShirtConfiguration).totalQuantity ?? 0) > 0;
    }
    else
    {
        return ((lineItem.unitPriceConfiguration as chooseYourOwnPriceConfiguration).defaultPrice.value.float ?? 0) > 0;
    }
}

/**
 * Creates an event listener for whenever the size of the cart is changed.
 * @param callback function to call when the size of the cart is changed.
 * @returns Cleanup function to remove the event listener
 */
export const cartSizeEventListener = ( callback: (event: Event) => void ) : (() => void) => 
{
    window.addEventListener('ShoppingCartSizeChange', callback);
    return () => { window.removeEventListener('ShoppingCartSizeChange', callback)}
}

/**
 * Creates an event listener for whenever the size of the cart is changed.
 * @param callback function to call when the size of the cart is changed.
 * @returns Cleanup function to remove the event listener
 */
export const cartUpdateEventListener = ( callback: (event: Event) => void ) : (() => void) => 
{
    window.addEventListener('ShoppingCartUpdateChange', callback);
    return () => { window.removeEventListener('ShoppingCartUpdateChange', callback)}
}